import { useState } from 'react';
import { RiNotification3Line } from 'react-icons/ri';
import { type BrowserPushSubscriptionPayload, getBrowserPushPublicKey, sendBrowserPushTest } from '@/api/integrations';
import { Button } from '@/components/primitives/button';
import { Card, CardContent, CardHeader } from '@/components/primitives/card';
import { showErrorToast, showSuccessToast } from '@/components/primitives/sonner-helpers';
import { useEnvironment } from '@/context/environment/hooks';

const SERVICE_WORKER_PATH = '/pingr-notify-push-sw.js';
const PUSH_OPERATION_TIMEOUT_MS = 15000;

async function withTimeout<T>(promise: Promise<T>, message: string): Promise<T> {
  let timeoutId: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<never>((_, reject) => {
    timeoutId = setTimeout(() => reject(new Error(message)), PUSH_OPERATION_TIMEOUT_MS);
  });

  try {
    return await Promise.race([promise, timeout]);
  } finally {
    if (timeoutId) clearTimeout(timeoutId);
  }
}

function isSecureBrowserContext() {
  if (typeof window === 'undefined') {
    return false;
  }

  return window.isSecureContext || window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
}

async function ensurePushServiceWorkerRegistration() {
  const existingRegistration = await navigator.serviceWorker.getRegistration('/');

  if (existingRegistration) {
    const activeScriptUrl = existingRegistration.active?.scriptURL || '';
    if (activeScriptUrl.endsWith(SERVICE_WORKER_PATH)) {
      await existingRegistration.update();

      return navigator.serviceWorker.ready;
    }

    await existingRegistration.unregister();
  }

  await navigator.serviceWorker.register(SERVICE_WORKER_PATH, { scope: '/' });

  // PushManager requires an active worker; register() can resolve while it is still installing.
  return navigator.serviceWorker.ready;
}

function base64UrlToUint8Array(value: string): Uint8Array {
  const padding = '='.repeat((4 - (value.length % 4)) % 4);
  const base64 = `${value}${padding}`.replace(/-/g, '+').replace(/_/g, '/');
  const raw = window.atob(base64);
  const output = new Uint8Array(raw.length);

  for (let i = 0; i < raw.length; i += 1) {
    output[i] = raw.charCodeAt(i);
  }

  return output;
}

function toSubscriptionPayload(subscription: PushSubscription): BrowserPushSubscriptionPayload {
  const json = subscription.toJSON();
  const endpoint = json.endpoint;
  const p256dh = json.keys?.p256dh;
  const auth = json.keys?.auth;

  if (!endpoint || !p256dh || !auth) {
    throw new Error('Browser push subscription is missing required endpoint/keys fields.');
  }

  return {
    endpoint,
    expirationTime: typeof json.expirationTime === 'number' ? json.expirationTime : null,
    keys: {
      p256dh,
      auth,
    },
  };
}

function hasMatchingApplicationServerKey(subscription: PushSubscription, expectedKey: Uint8Array): boolean {
  const configuredKey = subscription.options.applicationServerKey;
  if (!configuredKey) return false;

  const currentKey = new Uint8Array(configuredKey);
  return currentKey.length === expectedKey.length && currentKey.every((value, index) => value === expectedKey[index]);
}

type PushDeliveryOutcome = { received: boolean; displayed: boolean };

/**
 * Waits for the service worker to confirm (via postMessage) that the push
 * actually arrived at this browser and whether the OS rendered a visible
 * notification. This lets us distinguish a genuine success from the very common
 * "push accepted, but Windows Focus Assist / disabled Chrome notifications
 * swallowed the popup" case, instead of always claiming success.
 */
function waitForPushDelivery(timeoutMs: number): Promise<PushDeliveryOutcome> {
  return new Promise((resolve) => {
    if (typeof navigator === 'undefined' || !navigator.serviceWorker) {
      resolve({ received: false, displayed: false });

      return;
    }

    const outcome: PushDeliveryOutcome = { received: false, displayed: false };
    let settled = false;

    const finish = () => {
      if (settled) return;
      settled = true;
      navigator.serviceWorker.removeEventListener('message', handleMessage);
      clearTimeout(timeoutId);
      resolve(outcome);
    };

    const handleMessage = (event: MessageEvent) => {
      const data = event.data as { type?: string; displayed?: boolean } | undefined;
      if (!data || typeof data.type !== 'string') return;

      if (data.type === 'pingr-push-received') {
        outcome.received = true;
      } else if (data.type === 'pingr-push-displayed') {
        outcome.received = true;
        outcome.displayed = Boolean(data.displayed);
        finish();
      }
    };

    const timeoutId = setTimeout(finish, timeoutMs);
    navigator.serviceWorker.addEventListener('message', handleMessage);
  });
}

export function BrowserPushTestCard() {
  const { currentEnvironment } = useEnvironment();
  const [isSending, setIsSending] = useState(false);
  const [statusMessage, setStatusMessage] = useState<string | null>(null);
  const [statusType, setStatusType] = useState<'success' | 'error' | null>(null);

  const handleSendTestPush = async () => {
    if (isSending) {
      return;
    }

    setIsSending(true);
    setStatusMessage(null);
    setStatusType(null);

    try {
      if (typeof window === 'undefined' || typeof navigator === 'undefined') {
        throw new Error('Browser push notifications are only available in the browser.');
      }

      if (!currentEnvironment) {
        throw new Error('No active environment selected.');
      }

      if (!('Notification' in window)) {
        throw new Error('This browser does not support notifications.');
      }

      if (!('serviceWorker' in navigator)) {
        throw new Error('Service workers are not available in this browser.');
      }

      if (!isSecureBrowserContext()) {
        throw new Error('Push notifications require HTTPS (or localhost for development).');
      }

      const { vapidPublicKey } = await withTimeout(
        getBrowserPushPublicKey({ environment: currentEnvironment }),
        'Timed out while loading the Web Push public key.'
      );
      if (!vapidPublicKey) {
        throw new Error('Active Web Push integration is missing a VAPID public key.');
      }

      const permission =
        Notification.permission === 'granted'
          ? 'granted'
          : await withTimeout(
              Notification.requestPermission(),
              'Notification permission request timed out. Check the browser permission prompt.'
            );

      if (permission !== 'granted') {
        throw new Error('Notification permission was not granted.');
      }

      const registration = await withTimeout(
        ensurePushServiceWorkerRegistration(),
        'Timed out while registering the pingr-notify service worker.'
      );
      const applicationServerKey = base64UrlToUint8Array(vapidPublicKey);
      let subscription = await withTimeout(
        registration.pushManager.getSubscription(),
        'Timed out while checking the existing browser push subscription.'
      );

      if (subscription && !hasMatchingApplicationServerKey(subscription, applicationServerKey)) {
        await withTimeout(
          subscription.unsubscribe(),
          'Timed out while replacing an outdated browser push subscription.'
        );
        subscription = null;
      }

      if (!subscription) {
        subscription = await withTimeout(
          registration.pushManager.subscribe({
            userVisibleOnly: true,
            applicationServerKey,
          }),
          'Browser push registration timed out. Check browser notification permissions and network access.'
        );
      }

      // Start listening for the service worker's delivery confirmation BEFORE we
      // ask the server to send, so we never miss a fast round-trip.
      const deliveryPromise = waitForPushDelivery(PUSH_OPERATION_TIMEOUT_MS);

      await withTimeout(
        sendBrowserPushTest({
          environment: currentEnvironment,
          payload: {
            subscription: toSubscriptionPayload(subscription),
            title: 'Pingr Push Test',
            body: 'Real-time browser push from your self-hosted Pingr platform.',
            url: window.location.href,
          },
        }),
        'Timed out while sending the test push from Pingr.'
      );

      const delivery = await deliveryPromise;

      if (delivery.received && delivery.displayed) {
        setStatusType('success');
        setStatusMessage('Delivered — a real notification was shown by this browser.');
        showSuccessToast('Real-time test push delivered to this browser.');
      } else if (delivery.received && !delivery.displayed) {
        // Push reached the service worker, but the OS did not render the popup.
        setStatusType('error');
        setStatusMessage(
          'Push reached this browser, but your operating system did not display it. ' +
            'On Windows, turn off Focus Assist / Do Not Disturb and enable notifications for your browser ' +
            'in Settings → System → Notifications, then try again.'
        );
        showErrorToast('Push reached the browser but the OS suppressed the notification popup.');
      } else {
        // Server accepted the push, but no confirmation arrived from the service worker.
        setStatusType('success');
        setStatusMessage(
          'Test push accepted by the Web Push service. If no notification appears within a few seconds, ' +
            'check your OS notification settings (Focus Assist / browser notifications).'
        );
        showSuccessToast('Real-time test push sent to this browser.');
      }
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Failed to send test push notification.';
      setStatusType('error');
      setStatusMessage(message);
      showErrorToast(message);
    } finally {
      setIsSending(false);
    }
  };

  return (
    <Card className="w-full overflow-hidden shadow-none">
      <CardHeader>
        Built-in Browser Push
        <p className="text-foreground-500 mt-1 text-xs font-normal">
          Test Chrome/browser push through this platform&apos;s own Web Push pipeline (no third-party push connector
          required).
        </p>
      </CardHeader>
      <CardContent className="rounded-b-xl border-t bg-white p-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <p className="text-foreground-500 text-xs">
            Click once to request permission, then send a real server push notification to this browser.
          </p>
          <Button
            size="sm"
            variant="secondary"
            mode="outline"
            leadingIcon={RiNotification3Line}
            disabled={isSending}
            isLoading={isSending}
            onClick={handleSendTestPush}
          >
            {isSending ? 'Sending push...' : 'Send real-time test push'}
          </Button>
        </div>
        {statusMessage && (
          <p className={`mt-3 text-xs ${statusType === 'success' ? 'text-emerald-600' : 'text-red-600'}`}>
            {statusMessage}
          </p>
        )}
      </CardContent>
    </Card>
  );
}
